import { useMemo } from 'react';
import { useForm, useList } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, BookOpen, Pencil, DollarSign, Boxes, CalendarDays, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormHeader } from '@/components/forms';
import { TabsWithPersistence } from '@/components/shared';
import type { TranslationLanguage } from '@/components/translations';
import { type Course, getCourseTitle } from './types';
import { CourseEditTab } from './components/CourseEditTab';
import { CoursePricingTab } from './components/CoursePricingTab';
import { CourseModulesTab } from './components/CourseModulesTab';
import { CourseScheduleTab } from './components/CourseScheduleTab';
import { CourseMeetingTab } from './components/CourseMeetingTab';
import { CourseCreateEditTab } from './components/CourseCreateEditTab';

const SCHEDULE_COURSE_TYPES = new Set(['ONLINE', 'MEETING', 'WEBINAR']);

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
}

export function CoursesForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { formLoading, onFinish, query } = useForm<Course>({
    resource: 'courses',
    action: isEdit ? 'edit' : 'create',
    id,
    redirect: 'show',
  });

  const course = query?.data?.data;

  const { result: langResult, query: langQ } = useList<LanguageRecord>({ resource: 'languages' });
  const languages: TranslationLanguage[] = (langResult?.data ?? []).map((l) => ({
    code: l.code, name: l.name, nativeName: l.nativeName, flag: l.flag ?? l.flagEmoji ?? '🏳️',
  }));

  const refetch = query?.refetch;

  const tabs = useMemo(() => {
    if (isEdit && !course) { return []; }

    const items = [];

    if (isEdit && course) {
      // Режим редактирования: табы как в show, но без аналитики
      items.push({
        value: 'edit',
        label: 'Редактирование',
        icon: <Pencil className="h-4 w-4" />,
        content: (
          <CourseEditTab
            course={course}
            onUpdated={() => { refetch?.(); }}
          />
        ),
      });

      if (course.courseType !== 'MEETING') {
        items.push({
          value: 'pricing',
          label: 'Тарифы',
          icon: <DollarSign className="h-4 w-4" />,
          content: <CoursePricingTab courseId={course.id} />,
        });
      }

      items.push({
        value: 'modules',
        label: 'Модули',
        icon: <Boxes className="h-4 w-4" />,
        content: (
          <CourseModulesTab
            course={course}
            onRefresh={() => { refetch?.(); }}
          />
        ),
      });

      if (SCHEDULE_COURSE_TYPES.has(course.courseType)) {
        items.push({
          value: 'schedule',
          label: 'Расписание',
          icon: <CalendarDays className="h-4 w-4" />,
          content: (
            <CourseScheduleTab
              course={course}
              onUpdated={() => { refetch?.(); }}
            />
          ),
        });
      }

      if (course.courseType === 'MEETING') {
        items.push({
          value: 'meeting',
          label: 'Клуб',
          icon: <Users className="h-4 w-4" />,
          content: (
            <CourseMeetingTab
              course={course}
              onUpdated={() => { refetch?.(); }}
            />
          ),
        });
      }
    } else {
      // Режим создания: только таб редактирования, остальные disabled
      items.push({
        value: 'edit',
        label: 'Редактирование',
        icon: <Pencil className="h-4 w-4" />,
        content: (
          <CourseCreateEditTab
            loading={formLoading || langQ.isLoading}
            languages={languages}
            onFinish={async (v) => { await onFinish(v); }}
          />
        ),
      });

      items.push({
        value: 'pricing',
        label: 'Тарифы',
        icon: <DollarSign className="h-4 w-4" />,
        disabled: true,
        content: <div />,
      });

      items.push({
        value: 'modules',
        label: 'Модули',
        icon: <Boxes className="h-4 w-4" />,
        disabled: true,
        content: <div />,
      });
    }

    return items;
  }, [course, isEdit, refetch, formLoading, langQ.isLoading, languages, onFinish]);

  if (isEdit && query?.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isEdit && !course) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Курс не найден</p>
        <Button variant="link" onClick={() => navigate('/courses')}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const title = isEdit && course ? getCourseTitle(course) : 'Создать курс';

  return (
    <div className="space-y-6">
      <FormHeader
        title={title}
        onBack={() => navigate('/courses')}
        icon={<BookOpen className="h-5 w-5" />}
        titleAddon={
          isEdit && course ? (
            course.isPublished ? (
              <Badge className="bg-green-500/10 text-green-500">Опубликован</Badge>
            ) : (
              <Badge variant="secondary">Черновик</Badge>
            )
          ) : undefined
        }
        sticky
      />

      <TabsWithPersistence
        tabs={tabs}
        defaultTab="edit"
        persistKey={isEdit ? 'courses-edit' : 'courses-create'}
        tabsListClassName={
          tabs.length >= 5
            ? 'grid w-full grid-cols-3 sm:grid-cols-5 lg:inline-flex lg:w-auto'
            : (tabs.length >= 4
              ? 'grid w-full grid-cols-2 sm:grid-cols-4 lg:inline-flex lg:w-auto'
              : 'grid w-full grid-cols-3 lg:inline-flex lg:w-auto')
        }
      />
    </div>
  );
}
